import { useMemo } from 'react';
import { usePets } from './usePets';
import type { Pet } from '../types';

export interface UsePublicPetsResult {
  /** First dog + first cat from the visible pool, in that order. */
  featured: Pet[];
  /** Next 4 pets after the featured, excluding featured. */
  catalog: Pet[];
  /** First 8 distinct names for the ticker strip. */
  ticker: string[];
  /** Total available count (post status/archived filter on the page we fetched). */
  totalElements: number;
  loading: boolean;
  error: Error | null;
  /** True when no pets are loaded and we're not still fetching. */
  isEmpty: boolean;
}

/**
 * Drives the public Home page's Pets section.
 * Fetches up to 20 pets in one call and picks the editorial slices the
 * design wants. The hero cover is NOT picked from here — it stays
 * editorially controlled in HERO_COVER.
 */
export function usePublicPets(): UsePublicPetsResult {
  const { pets, totalElements, loading, error } = usePets({ size: 20 });

  const { featured, catalog, ticker } = useMemo(() => {
    // Editorial intent: "duas fichas abertas". Prefer one dog + one cat;
    // when the pool is single-kind, fall back to the first two distinct pets
    // so the section never reads as half-rendered.
    const firstDog = pets.find((p) => p.kind === 'dog');
    const firstCat = pets.find((p) => p.kind === 'cat');
    const featuredArr: Pet[] = [];
    if (firstDog) featuredArr.push(firstDog);
    if (firstCat && firstCat.id !== firstDog?.id) featuredArr.push(firstCat);
    if (featuredArr.length < 2) {
      for (const p of pets) {
        if (featuredArr.some((f) => f.id === p.id)) continue;
        featuredArr.push(p);
        if (featuredArr.length === 2) break;
      }
    }

    const featuredIds = new Set(featuredArr.map((p) => p.id));
    const catalogArr = pets.filter((p) => !featuredIds.has(p.id)).slice(0, 4);

    const tickerSet = new Set<string>();
    for (const p of pets) {
      tickerSet.add(p.name);
      if (tickerSet.size >= 8) break;
    }

    return {
      featured: featuredArr,
      catalog: catalogArr,
      ticker: Array.from(tickerSet),
    };
  }, [pets]);

  return {
    featured,
    catalog,
    ticker,
    totalElements,
    loading,
    error,
    isEmpty: !loading && pets.length === 0,
  };
}
